import { Outlet, useRouterState, Link } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'
import { Check, Loader2, Menu, Plus, RefreshCw } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  Sheet,
  SheetContent,
  SheetTitle,
} from '@/components/ui/sheet'
import { Sidebar, NAV } from '@/components/sidebar'
import { ThemeToggle } from '@/components/theme-toggle'
import { LiveIndicator } from '@/components/live-indicator'
import { LiveStatusProvider } from '@/components/live-status-context'
import { useRunsLive } from '@/features/runs/use-runs-live'
import {
  TriggerDialogProvider,
  useTriggerDialog,
} from '@/features/trigger/trigger-dialog'
import { cn } from '@/lib/utils'

const SIDEBAR_KEY = 'litmers.sidebar.collapsed'

function useBreadcrumb() {
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const params = useRouterState({ select: (s) => s.matches.at(-1)?.params ?? {} })
  return useMemo(() => {
    const trail: Array<{ label: string; to?: string; mono?: boolean }> = []
    const top = NAV.find(
      (n) => pathname === n.to || pathname.startsWith(`${n.to}/`),
    )
    if (top) trail.push({ label: top.label, to: top.to })
    if (pathname.startsWith('/runs/') && pathname !== '/runs') {
      const p = params as { runId?: string }
      const id = p.runId
      if (id) trail.push({ label: id.slice(0, 12), mono: true })
    }
    if (trail.length === 0) trail.push({ label: 'Litmers' })
    return trail
  }, [pathname, params])
}

function LiveBaseline() {
  useRunsLive()
  return null
}

function isSseCoveredRoute(pathname: string): boolean {
  return pathname === '/runs' || pathname.startsWith('/runs/')
}

function RefreshButton() {
  const queryClient = useQueryClient()
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const [refreshing, setRefreshing] = useState(false)
  const [justSynced, setJustSynced] = useState(false)

  if (isSseCoveredRoute(pathname)) return null

  const onClick = async () => {
    setRefreshing(true)
    setJustSynced(false)
    try {
      await queryClient.invalidateQueries({ refetchType: 'active' })
    } finally {
      setRefreshing(false)
      setJustSynced(true)
      setTimeout(() => setJustSynced(false), 1500)
    }
  }

  const label = refreshing ? 'Refreshing…' : justSynced ? 'Synced' : 'Refresh'
  const Icon = refreshing ? Loader2 : justSynced ? Check : RefreshCw

  return (
    <TooltipProvider delayDuration={400}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClick}
            disabled={refreshing}
            aria-label="Refresh data"
            className={cn(
              'gap-1.5',
              justSynced
                ? 'text-emerald-600 dark:text-emerald-400 hover:text-emerald-600 dark:hover:text-emerald-400'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <Icon className={cn('size-3.5', refreshing && 'animate-spin')} />
            <span className="hidden sm:inline">{label}</span>
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          Refetch data on this page
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

const GitHubIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" aria-hidden focusable={false} {...props}>
    <path
      fill="currentColor"
      d="M12 .5C5.65.5.5 5.66.5 12.04c0 5.1 3.29 9.41 7.86 10.94.58.1.79-.25.79-.56v-2.18c-3.2.7-3.87-1.36-3.87-1.36-.52-1.33-1.27-1.69-1.27-1.69-1.04-.71.08-.7.08-.7 1.15.08 1.76 1.18 1.76 1.18 1.02 1.76 2.69 1.25 3.34.96.1-.74.4-1.25.72-1.54-2.55-.29-5.24-1.28-5.24-5.69 0-1.26.45-2.29 1.18-3.1-.12-.29-.51-1.46.11-3.05 0 0 .96-.31 3.16 1.18.92-.26 1.9-.39 2.88-.39.98 0 1.96.13 2.88.39 2.2-1.49 3.16-1.18 3.16-1.18.62 1.59.23 2.76.11 3.05.74.81 1.18 1.84 1.18 3.1 0 4.42-2.69 5.4-5.25 5.69.41.36.78 1.06.78 2.13v3.16c0 .31.21.67.8.55C20.21 21.45 23.5 17.13 23.5 12.04 23.5 5.66 18.35.5 12 .5z"
    />
  </svg>
)

export function AppShell() {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false
    return window.localStorage.getItem(SIDEBAR_KEY) === '1'
  })
  const [drawerOpen, setDrawerOpen] = useState(false)
  const breadcrumb = useBreadcrumb()
  const pathname = useRouterState({ select: (s) => s.location.pathname })

  useEffect(() => {
    setDrawerOpen(false)
  }, [pathname])

  useEffect(() => {
    window.localStorage.setItem(SIDEBAR_KEY, collapsed ? '1' : '0')
  }, [collapsed])

  return (
    <LiveStatusProvider>
    <TriggerDialogProvider>
    <LiveBaseline />
    <div className="flex min-h-screen bg-background">
      <div className="hidden md:flex">
        <Sidebar
          collapsed={collapsed}
          onToggle={() => setCollapsed((v) => !v)}
        />
      </div>

      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent side="left" className="w-64 p-0 [&>button]:hidden">
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <Sidebar
            collapsed={false}
            variant="drawer"
            onToggle={() => undefined}
            onItemClick={() => setDrawerOpen(false)}
          />
        </SheetContent>
      </Sheet>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-border/70 bg-background/80 px-3 backdrop-blur supports-[backdrop-filter]:bg-background/70 md:px-5">
          <div className="flex min-w-0 items-center gap-2">
            <Button
              variant="ghost"
              size="icon-sm"
              className="md:hidden"
              onClick={() => setDrawerOpen(true)}
              aria-label="Open navigation"
            >
              <Menu />
            </Button>
            <ol className="flex min-w-0 items-center gap-1.5 text-sm">
              {breadcrumb.map((item, i) => (
                <li key={`${item.label}-${i}`} className="flex items-center gap-1.5 min-w-0">
                  {i > 0 ? (
                    <span className="text-muted-foreground/60">/</span>
                  ) : null}
                  {item.to ? (
                    <Link
                      to={item.to}
                      className={cn(
                        'truncate rounded-sm px-1 py-0.5 hover:bg-accent/40',
                        i === breadcrumb.length - 1
                          ? 'text-foreground font-medium'
                          : 'text-muted-foreground',
                      )}
                    >
                      {item.label}
                    </Link>
                  ) : (
                    <span
                      className={cn(
                        'truncate px-1 py-0.5',
                        item.mono && 'font-mono text-[12.5px]',
                        i === breadcrumb.length - 1
                          ? 'text-foreground font-medium'
                          : 'text-muted-foreground',
                      )}
                    >
                      {item.label}
                    </span>
                  )}
                </li>
              ))}
            </ol>
          </div>

          <div className="flex items-center gap-1.5">
            <TopbarTriggerButton />
            <span className="mx-1 hidden sm:block h-4 w-px bg-border/70" />
            <LiveIndicator />
            <RefreshButton />
            <a
              href="https://github.com/litmers-dev"
              target="_blank"
              rel="noreferrer"
              className="inline-flex size-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
              aria-label="GitHub"
            >
              <GitHubIcon className="size-4" />
            </a>
            <ThemeToggle />
          </div>
        </header>

        <main className="flex-1 min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
    </TriggerDialogProvider>
    </LiveStatusProvider>
  )
}

function TopbarTriggerButton() {
  const { openTrigger } = useTriggerDialog()
  return (
    <>
      <Button
        size="sm"
        onClick={() => openTrigger()}
        className="hidden sm:inline-flex gap-1.5"
      >
        <Plus />
        New run
      </Button>
      <Button
        size="icon-sm"
        onClick={() => openTrigger()}
        className="sm:hidden"
        aria-label="Trigger run"
      >
        <Plus />
      </Button>
    </>
  )
}
